import { queryOptions } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import type { Disclosure, Fact, PublicListing } from './types';

type PublicLocation = components['schemas']['PublicLocation'];

/** Page size cap of listPublicLocations. */
const PAGE_LIMIT = 50;
/** Explore filters and sorts client side, so it loads the catalog up to this many listings. */
export const CATALOG_CAP = 200;

export interface PublicCatalog {
  listings: PublicListing[];
  /** True when the catalog holds more listings than CATALOG_CAP. */
  truncated: boolean;
}

/**
 * The contract types a fact as `{ disclosure, value? }`. Narrow it to the local
 * discriminated union: a value is kept only for known facts, and a known fact
 * without a value is shown as unknown rather than trusted.
 */
function toFact<T>(fact: { disclosure: Disclosure; value?: T }): Fact<T> {
  if (fact.disclosure !== 'known') return { disclosure: fact.disclosure };
  return fact.value === undefined ? { disclosure: 'unknown' } : { disclosure: 'known', value: fact.value };
}

export function toPublicListing(l: PublicLocation): PublicListing {
  return {
    release_id: l.release_id,
    public_slug: l.public_slug,
    title: l.title,
    summary: l.summary,
    country_code: l.country_code,
    region: l.region,
    coarse_locality: l.coarse_locality,
    site_category: l.site_category,
    capacity: toFact(l.capacity),
    expansion: toFact(l.expansion),
    municipal_approval: toFact(l.municipal_approval),
    grid_connection: toFact(l.grid_connection),
    power_categories: toFact(l.power_categories),
    network_categories: toFact(l.network_categories),
    service_categories: l.service_categories,
    availability: l.availability,
    highlights: l.highlights,
    verified_at: l.verified_at,
    published_at: l.published_at,
    reconfirm_by: l.reconfirm_by,
  };
}

const publicKey = (...parts: unknown[]) => ['public', 'locations', ...parts];

/** Published catalog, following the cursor until it ends or reaches CATALOG_CAP. */
export function publicCatalogQuery() {
  return queryOptions({
    queryKey: publicKey('catalog'),
    queryFn: async ({ signal }): Promise<PublicCatalog> => {
      const listings: PublicListing[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await unwrap(
          api.GET('/api/v1/public/locations', { params: { query: { limit: PAGE_LIMIT, cursor } }, signal }),
        );
        listings.push(...page.items.map(toPublicListing));
        const next = page.page.has_more ? page.page.next_cursor : null;
        if (!next) return { listings, truncated: false };
        if (listings.length >= CATALOG_CAP) return { listings: listings.slice(0, CATALOG_CAP), truncated: true };
        cursor = next;
      }
    },
  });
}

/** One published listing. 404 is unknown, 410 is withdrawn. */
export function publicListingQuery(slug: string) {
  return queryOptions({
    queryKey: publicKey('one', slug),
    queryFn: async ({ signal }) =>
      toPublicListing(
        await unwrap(
          api.GET('/api/v1/public/locations/{publicSlug}', { params: { path: { publicSlug: slug } }, signal }),
        ),
      ),
    // A 404 or 410 settled by the route loader stays settled; do not request it again on mount.
    retryOnMount: false,
  });
}
